import { useState, useEffect } from 'react';
import { payslipApi, PayslipInfo } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function MyPayslipsPage() {
  const [payslips, setPayslips] = useState<PayslipInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>('');

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    loadPayslips();
  }, [selectedYear]);

  const loadPayslips = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: { year?: string } = {};
      if (selectedYear) {
        params.year = selectedYear;
      }
      const response = await payslipApi.listPayslips(params);
      setPayslips(response.payslips || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load payslips');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (payslip: PayslipInfo) => {
    setDownloadingId(payslip.payslipId);
    try {
      const response = await payslipApi.getDownloadUrl(payslip.userId, payslip.year, payslip.month);
      // Open download in new tab
      window.open(response.downloadUrl, '_blank');
    } catch (err: any) {
      alert(err.message || 'Failed to download payslip');
    } finally {
      setDownloadingId(null);
    }
  };

  // Group payslips by year
  const groupedPayslips = payslips.reduce((acc, payslip) => {
    const year = payslip.year.toString();
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(payslip);
    return acc;
  }, {} as Record<string, PayslipInfo[]>);

  // Sort years descending
  const sortedYears = Object.keys(groupedPayslips).sort((a, b) => parseInt(b) - parseInt(a));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">My Payslips</h1>
          <p className="mt-1 text-sm text-gray-500">View and download your monthly payslips</p>
        </div>
        <div>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="">All Years</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={loadPayslips}
            className="mt-2 text-sm text-red-600 underline hover:text-red-800"
          >
            Try again
          </button>
        </div>
      ) : payslips.length === 0 ? (
        <div className="card p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No payslips available</h3>
          <p className="mt-2 text-sm text-gray-500">
            {selectedYear
              ? `No payslips found for ${selectedYear}`
              : 'Your payslips will appear here once they are uploaded'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedYears.map((year) => (
            <div key={year} className="card overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">{year}</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {groupedPayslips[year]
                  .sort((a, b) => b.month - a.month) // Most recent month first
                  .map((payslip) => (
                    <div
                      key={payslip.payslipId}
                      className="flex items-center justify-between px-4 py-4 hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {MONTH_NAMES[payslip.month - 1]} {payslip.year}
                          </p>
                          <p className="text-xs text-gray-500">
                            Uploaded {new Date(payslip.uploadedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(payslip)}
                        disabled={downloadingId === payslip.payslipId}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {downloadingId === payslip.payslipId ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">Downloading...</span>
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </>
                        )}
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
